<template>
  <Drawer
    v-if="sheet"
    placement="bottom"
    :title="title"
    :aria-label="title"
    @hide-drawer="emit('close')"
  >
    <div class="px-4 pb-4">
      <div v-if="$slots.header" class="mb-2">
        <slot name="header"></slot>
      </div>
      <slot></slot>
    </div>
    <template #footer>
      <slot name="footer"></slot>
    </template>
  </Drawer>
  <Modal v-else :title="title" @hide-modal="emit('close')">
    <template #header>
      <slot name="header"></slot>
    </template>
    <slot></slot>
    <template #footer>
      <slot name="footer"></slot>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import Modal from "@/components/Modal.vue";
import Drawer from "@/components/Seranova/Drawer/Drawer.vue";

/**
 * Chooses the surface a calendar event's form is presented in, so the form
 * itself does not have to know: a bottom sheet on a phone, the existing modal
 * everywhere else. Only the wrapper changes -- the ~450 lines of validation and
 * save logic in CalendarEventModal stay where they are.
 *
 * `sheet` is passed in rather than derived here. The decision belongs to the
 * call site because CalendarEventModal is reachable from the classic schedule
 * board too (via CreateNewFOB -> CreateEventButton), and deriving it from
 * viewport width inside this component would put a sheet on that page as a side
 * effect. Opting in mirrors how AppointmentDetailsDrawer takes `placement`.
 *
 * Slot mapping is not one-to-one: Modal has a `header` slot and Drawer does not,
 * so on the sheet branch that content is rendered at the top of the body
 * instead. Both surfaces get one `close` event, hiding the fact that they emit
 * `hide-modal` and `hide-drawer` respectively.
 */
withDefaults(
  defineProps<{
    title?: string;
    sheet?: boolean;
  }>(),
  { title: "", sheet: false }
);

const emit = defineEmits<{ (e: "close"): void }>();
</script>
